import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useSearchParams } from 'react-router-dom';

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useFraudHubSeverityTrend } from '@/api/hooks/useFraudHubSeverityTrend';
import { SEVERITY_FILL, SEVERITY_ORDER } from '@/lib/severity';
import { parsePeriod } from '@/lib/timePeriod';
import type { Severity } from '@/types/alerts';
import type { SeverityTrendDay } from '@/types/fraudHub';

/**
 * Section 2 of the Fraud Hub: a 30-day stacked-bar chart of alert
 * volume per severity tier.
 *
 * Severity-first encoding rule: Critical sits at the BOTTOM of every
 * stacked bar so the eye lands on the loudest tier first. That means
 * we render Recharts <Bar> series in REVERSE severity order — Recharts
 * stacks in render order with the first <Bar> at the bottom.
 *
 * The wire payload omits 'Normal' (no production alerts emit it) and
 * the legend includes it as 0 anyway for forward-compatibility, hence
 * the SEVERITY_ORDER iteration rather than hard-coded keys.
 *
 * X-axis density: with 30 daily buckets, rendering every label causes
 * overlap on narrow viewports. We compute an `interval` so ~7 ticks
 * are shown — enough to anchor the eye, not so many they collide.
 */

const NEUTRAL_FILL = 'hsl(215 20% 65%)';

/** Project the raw API row into a chart row with every severity as a key
 *  (including Normal=0) so legends render consistently. */
function toChartRow(
  d: SeverityTrendDay,
): { date: string } & Record<Severity, number> {
  return {
    date: d.date,
    Critical: d.critical,
    High: d.high,
    Medium: d.medium,
    Low: d.low,
    Watch: d.watch,
    Normal: 0,
  };
}

/** Render 'YYYY-MM-DD' as a short weekday + day-of-month, e.g. 'Mon 5'. */
function formatXTick(date: string): string {
  // Parse as UTC midnight and render in the viewer's locale; the date
  // itself is already day-bucketed so timezone offset doesn't matter.
  const d = new Date(`${date}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return date;
  const weekday = d.toLocaleDateString('en-ZA', {
    weekday: 'short',
    timeZone: 'UTC',
  });
  return `${weekday} ${d.getUTCDate()}`;
}

export function SeverityTrendChart(): JSX.Element {
  const [searchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'), 'M');
  const { data, isLoading, isError, error } = useFraudHubSeverityTrend(period);

  // Backend echoes the resolved label ("10 May 2026 (Day)" / "May 2026 MTD"
  // / etc.) so we don't have to re-derive it client-side from the period.
  const periodLabel = data?.period_label ?? '';

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">
          Severity Trend{periodLabel ? ` — ${periodLabel}` : ''}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {isLoading && <Skeleton className="h-[260px] w-full" />}
        {!isLoading && isError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
            Failed to load severity trend: {error?.message ?? 'unknown error'}
          </div>
        )}
        {!isLoading && !isError && data && <ChartBody days={data.days} />}
      </CardContent>
    </Card>
  );
}

function ChartBody({ days }: { days: SeverityTrendDay[] }): JSX.Element {
  const chartRows = days.map(toChartRow);
  const totalAcrossWindow = days.reduce(
    (sum, d) => sum + d.critical + d.high + d.medium + d.low + d.watch,
    0,
  );

  if (totalAcrossWindow === 0) {
    return (
      <div className="flex h-[260px] flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
        <p className="font-medium">No alerts in this window</p>
        <p className="text-xs">
          The chart lights up automatically once new alerts land.
        </p>
      </div>
    );
  }

  // Render Bars in REVERSE severity order so Critical lands at the
  // bottom of each stacked bar (Recharts stacks first <Bar> at bottom).
  const reversed = [...SEVERITY_ORDER].reverse();

  // With ~30 daily buckets the X-axis collides if every label renders;
  // pick an interval that yields ~7 visible ticks (one per ~week) and
  // still shows the most-recent date. interval=0 forces every tick;
  // interval=N skips N labels between rendered ones.
  const xAxisInterval = Math.max(0, Math.floor(chartRows.length / 7) - 1);

  return (
    <div className="h-[260px]">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartRows}
          margin={{ top: 8, right: 8, bottom: 4, left: 0 }}
        >
          <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10 }}
            tickFormatter={formatXTick}
            interval={xAxisInterval}
          />
          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
          <Tooltip
            contentStyle={{
              background: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              fontSize: 12,
            }}
            labelFormatter={(label: string) => formatXTick(label)}
          />
          <Legend
            wrapperStyle={{ fontSize: 12 }}
            iconType="square"
            // Match canonical SEVERITY_ORDER (Critical → Normal) in the
            // legend regardless of the reversed render order on bars.
            payload={SEVERITY_ORDER.map((sev) => ({
              value: sev,
              type: 'square' as const,
              id: sev,
              color: SEVERITY_FILL[sev] ?? NEUTRAL_FILL,
            }))}
          />
          {reversed.map((sev) => (
            // toChartRow projects each row with the Severity name as the key
            // (Critical/High/…/Normal=0), so the bar reads it directly. Using
            // the lowercase wire key here was the bug that left every bar empty.
            <Bar
              key={sev}
              dataKey={sev}
              stackId="severity"
              name={sev}
              fill={SEVERITY_FILL[sev] ?? NEUTRAL_FILL}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default SeverityTrendChart;
